import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IMAGES, CVC_TIERS, CVC_LADDER, CVC_MATCH, CVC_HISTORY } from '../mock/data';
import { Reveal, Eyebrow, SectionHeading } from '../components/xc/Bits';
import { InvestorNote } from '../components/xc/InvestorNote';
import { Swords, Trophy, ArrowLeft, ShieldCheck, Crown, ChevronUp, ChevronDown, Radio } from 'lucide-react';

const FEED_POOL = [
  { crew: 'a', text: 'Wolf lands a 240-point qualifying round' },
  { crew: 'b', text: 'Raven answers with a 210-point streak' },
  { crew: 'a', text: 'Anna88 chains three qualifying spins' },
  { crew: 'b', text: 'Kane pushes Crimson Order forward' },
  { crew: 'a', text: 'Marco banks a 180-point round' },
  { crew: 'b', text: 'Mila closes the gap with a big round' },
  { crew: 'a', text: 'Yordy triggers a crew bonus round' },
  { crew: 'b', text: 'Dux keeps the pressure on' },
];

function RoundPips({ won, color, count }) {
  return (
    <div className="flex gap-1.5">
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="h-1.5 w-6 rounded-full" style={{ background: i < won ? color : 'rgba(255,255,255,0.12)' }} />
      ))}
    </div>
  );
}

function LiveMatch() {
  const [scores, setScores] = useState({ a: 3120, b: 2870 });
  const [feed, setFeed] = useState([{ id: 0, crew: 'a', text: 'Round 4 is live — first to 5,000 points wins the round' }]);
  const idRef = useRef(1);

  useEffect(() => {
    const t = setInterval(() => {
      const ev = FEED_POOL[Math.floor(Math.random() * FEED_POOL.length)];
      const gain = 90 + Math.floor(Math.random() * 160);
      setScores((s) => {
        const next = { ...s, [ev.crew]: Math.min(CVC_MATCH.target, s[ev.crew] + gain) };
        if (next.a >= CVC_MATCH.target || next.b >= CVC_MATCH.target) return { a: 3120, b: 2870 };
        return next;
      });
      setFeed((f) => [{ id: idRef.current++, crew: ev.crew, text: ev.text }, ...f].slice(0, 5));
    }, 2600);
    return () => clearInterval(t);
  }, []);

  const side = (c, score, align) => (
    <div className={align === 'right' ? 'text-right' : ''}>
      <div className="flex items-center gap-2" style={{ flexDirection: align === 'right' ? 'row-reverse' : 'row' }}>
        <span className="h-9 w-9 rounded-lg grid place-items-center font-display font-700 text-white" style={{ background: `${c.color}22`, border: `1px solid ${c.color}55` }}>{c.name[0] === 'T' ? c.name[4] : c.name[0]}</span>
        <div>
          <div className="font-display font-700 text-white tracking-wide">{c.name}</div>
          <div className="text-[11px] text-white/40 uppercase tracking-wider">{c.players.join(' · ')}</div>
        </div>
      </div>
      <div className="mt-3 font-display font-700 text-3xl tabular-nums" style={{ color: c.color }}>{score.toLocaleString()}</div>
      <div className={`mt-2 flex ${align === 'right' ? 'justify-end' : ''}`}><RoundPips won={c.roundsWon} color={c.color} count={3} /></div>
    </div>
  );

  return (
    <div data-testid="cvc-live-match" className="xc-card rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
        <span className="flex items-center gap-2 text-[11px] font-600 tracking-[0.2em] text-[#FF204E]">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF204E] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF204E]" />
          </span>
          LIVE CONFRONTATION — ROUND {CVC_MATCH.round} OF {CVC_MATCH.bestOf}
        </span>
        <span className="text-xs text-white/40">First to {CVC_MATCH.target.toLocaleString()} pts</span>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
          {side(CVC_MATCH.a, scores.a, 'left')}
          <div className="text-center">
            <Swords className="h-8 w-8 text-white/30 mx-auto" />
            <div className="mt-1 font-display font-700 text-white/60 text-lg">{CVC_MATCH.a.roundsWon}–{CVC_MATCH.b.roundsWon}</div>
          </div>
          {side(CVC_MATCH.b, scores.b, 'right')}
        </div>
        <div className="mt-6 h-2.5 rounded-full bg-white/8 overflow-hidden flex">
          <div className="h-full transition-all duration-700" style={{ width: `${(scores.a / (scores.a + scores.b)) * 100}%`, background: `linear-gradient(90deg, ${CVC_MATCH.a.color}, ${CVC_MATCH.a.color}88)` }} />
          <div className="h-full flex-1 transition-all duration-700" style={{ background: `linear-gradient(90deg, ${CVC_MATCH.b.color}88, ${CVC_MATCH.b.color})` }} />
        </div>
        <div data-testid="cvc-live-feed" className="mt-5 space-y-2">
          {feed.map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-sm xc-fade-up">
              <Radio className="h-3.5 w-3.5 flex-shrink-0" style={{ color: f.crew === 'a' ? CVC_MATCH.a.color : CVC_MATCH.b.color }} />
              <span className="text-white/60">{f.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CrewVsCrew() {
  const nav = useNavigate();
  const tierColor = (t) => CVC_TIERS.find((x) => x.name === t)?.color || '#8a8a95';
  return (
    <div className="bg-[#070609] min-h-screen">
      <section className="relative overflow-hidden border-b border-white/5">
        <img src={IMAGES.redRoom} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 city-overlay" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 py-14">
          <button data-testid="cvc-back-btn" onClick={() => nav('/missions')} className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-6"><ArrowLeft className="h-4 w-4" /> Mission Board</button>
          <Eyebrow>Ranked Competition — Preview</Eyebrow>
          <h1 className="font-display font-700 text-3xl sm:text-4xl md:text-5xl text-white tracking-tight">Crew <span className="text-gradient-rp">VS</span> Crew</h1>
          <p className="mt-3 text-white/60 max-w-xl">Two crews. Best of five rounds. Every qualifying casino round your crew plays pushes your score — the maths of the games never change, only the story around them.</p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs text-white/40 xc-glass rounded-full px-4 py-2"><ShieldCheck className="h-3.5 w-3.5 text-[#2FBF71]" /> Investor preview — simulated confrontation with demo data. No real money.</div>
          <div className="mt-4"><InvestorNote id="cvc" title="Why ranked crews retain players">
            Ranked seasonal ladders are the highest-retention loop in competitive gaming. Applied to crews instead of individuals, wins and losses become shared — players return not for the games, but because their crew is counting on them tonight.
          </InvestorNote></div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-[1fr_400px] gap-6">
        <Reveal><LiveMatch /></Reveal>
        <div className="space-y-6">
          <Reveal delay={100}>
            <div data-testid="cvc-ladder" className="xc-card rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><Trophy className="h-5 w-5 text-[#E0B85A]" /><h3 className="font-display font-700 text-white">Season Ladder</h3></div>
              <div className="space-y-2">
                {CVC_LADDER.map((c) => (
                  <div key={c.rank} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${c.self ? 'bg-[#FF204E]/10 border border-[#FF204E]/30' : 'bg-white/[0.03]'}`}>
                    <span className="w-6 text-sm font-display font-700 text-white/50">#{c.rank}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-sm text-white font-500">{c.crew}{c.rank === 1 && <Crown className="h-3.5 w-3.5 text-[#E0B85A]" />}</div>
                      <div className="text-[11px] text-white/40">{c.wins}W – {c.losses}L</div>
                    </div>
                    <span className="text-[10px] font-600 tracking-wider uppercase px-2 py-0.5 rounded-full" style={{ color: tierColor(c.tier), border: `1px solid ${tierColor(c.tier)}55` }}>{c.tier}</span>
                    <span className="text-xs text-white/55 tabular-nums w-16 text-right">{c.rep.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between">
                {CVC_TIERS.map((t) => (
                  <div key={t.name} className="flex flex-col items-center gap-1">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color, boxShadow: `0 0 8px ${t.color}` }} />
                    <span className="text-[9px] uppercase tracking-wider text-white/40">{t.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={180}>
            <div data-testid="cvc-history" className="xc-card rounded-2xl p-6">
              <h3 className="font-display font-700 text-white mb-4">Recent Confrontations</h3>
              <div className="space-y-3">
                {CVC_HISTORY.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    {h.result === 'Victory' ? <ChevronUp className="h-4 w-4 text-[#2FBF71]" /> : <ChevronDown className="h-4 w-4 text-[#FF204E]" />}
                    <span className="flex-1 text-white/70">vs {h.opp}</span>
                    <span className="text-white/45">{h.score}</span>
                    <span className={h.result === 'Victory' ? 'text-[#2FBF71]' : 'text-[#FF204E]'}>{h.rep}</span>
                    <span className="text-white/30 text-xs w-16 text-right">{h.when}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 pb-16">
        <Reveal>
          <SectionHeading eyebrow="How it works" color="#B026FF" title="Fair by design" sub="Confrontation scores come only from settled, trusted game events supplied by licensed providers. Xincity never alters odds, reels or outcomes — crews simply compete on qualifying activity." />
        </Reveal>
      </section>
    </div>
  );
}
